/**
 * JSON-LD @graph generator for CIH V3 Guide detail pages
 * (/coaching-business/[slug]).
 *
 * Emits Article + FAQPage (when FAQs present) + BreadcrumbList + Organization
 * as a single @graph. FAQPage drives PAA snippets and AEO answers.
 *
 * No HowTo node: schema.org/HowTo requires `step`, and the node we emitted had
 * none, so it was invalid markup — and Google deprecated HowTo rich results in
 * 2023, so there was no upside to fixing it rather than dropping it.
 *
 * The Organization node is a stub that resolves to the canonical brand entity
 * via the shared BRAND_ORGANIZATION_ID; the fully-described Organization lives
 * in the site-wide schema. Both must use that exact @id or the Knowledge Graph
 * splits the brand into two entities.
 *
 * Aliases: guide jsonld, guide structured data, cbb guide schema
 */
import { BRAND, BRAND_ORGANIZATION_ID, BRAND_URL } from "~/lib/shared/brand";
import { DEFAULT_OG_IMAGE } from "~/lib/shared/meta";

type Faq = { readonly question: string; readonly answer: string };

export type GuideJsonLdInput = {
  readonly slug: string;
  readonly title: string;
  readonly description: string;
  readonly faqs: ReadonlyArray<Faq>;
  readonly heroImageUrl: string | null;
  readonly publishedAt: Date | null;
  readonly updatedAt: Date | null;
};

/**
 * Absolute URL for a stored hero image, or the brand default when absent.
 * Article `image` must be absolute — Google ignores relative values.
 */
function absoluteImageUrl(heroImageUrl: string | null): string {
  if (!heroImageUrl) return DEFAULT_OG_IMAGE;
  if (/^https?:\/\//.test(heroImageUrl)) return heroImageUrl;
  return `${BRAND_URL}${heroImageUrl.startsWith("/") ? "" : "/"}${heroImageUrl}`;
}

export function buildGuideJsonLd(input: GuideJsonLdInput) {
  const url = `${BRAND_URL}/coaching-business/${input.slug}`;
  const articleId = `${url}#article`;
  const breadcrumbId = `${url}#breadcrumb`;

  const datePublished = input.publishedAt?.toISOString();
  const dateModified =
    input.updatedAt?.toISOString() ?? input.publishedAt?.toISOString();

  const graph: Array<Record<string, unknown>> = [
    {
      "@type": "Organization",
      "@id": BRAND_ORGANIZATION_ID,
      name: BRAND.name,
      url: BRAND_URL,
      logo: {
        "@type": "ImageObject",
        url: BRAND.logo,
      },
    },
    {
      "@type": "Article",
      "@id": articleId,
      headline: input.title,
      description: input.description,
      url,
      image: absoluteImageUrl(input.heroImageUrl),
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      author: { "@id": BRAND_ORGANIZATION_ID },
      publisher: { "@id": BRAND_ORGANIZATION_ID },
      ...(datePublished ? { datePublished } : {}),
      ...(dateModified ? { dateModified } : {}),
      inLanguage: "en",
      articleSection: "Coach Business Builder",
    },
    {
      "@type": "BreadcrumbList",
      "@id": breadcrumbId,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: BRAND_URL },
        {
          "@type": "ListItem",
          position: 2,
          name: "Coaching Business",
          item: `${BRAND_URL}/coaching-business`,
        },
        { "@type": "ListItem", position: 3, name: input.title, item: url },
      ],
    },
    ...(input.faqs.length > 0
      ? [
          {
            "@type": "FAQPage",
            "@id": `${url}#faq`,
            mainEntity: input.faqs.map((f) => ({
              "@type": "Question",
              name: f.question,
              acceptedAnswer: { "@type": "Answer", text: f.answer },
            })),
          },
        ]
      : []),
  ];

  return { "@context": "https://schema.org", "@graph": graph };
}
